/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from 'react'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '~/components/UI/shadcn/accordion'
import sanitizeHtml from 'sanitize-html'

import TertiaryLink from '~/components/UI/Buttons/TertiaryLink/TertiaryLink'
import { cva } from 'class-variance-authority'
import { cn } from '~/lib/utils'
import { useQuery } from '@tanstack/react-query'
import { getEntriesByIds } from '~/lib/contensis/misc'
import Loader from '~/components/UI/General/Loader/Loader'

const faqVariants = cva('', {
  variants: {
    border: {
      'ink-blue': 'border-blue-d-40',
      'mid-blue': 'border-blue-m-40',
      white: 'border-white/60'
    },
    fill: {
      'ink-blue': 'fill-blue-d',
      'mid-blue': 'fill-blue-m',
      white: 'fill-white'
    },
    defaultVariant: { border: 'ink-blue', fill: 'ink-blue' }
  }
})

interface FaqProps {
  content: {
    heading?: string
    expandItem?: boolean
    items?: {
      heading: string
      copy: string
      sys?: { id?: string }
    }[]
    buttons?: {
      externalLink: string | null
      internalLink: { sys: { uri: string } }
      ariaLabel: string
      target: string
      label: string
      mobileLabel: string
      strongColour?: boolean
    }
  }
  layoutColour: AllBackgroundColours
}

const Faqs = ({ content, layoutColour }: FaqProps) => {
  const { heading, buttons, expandItem = true, items = [] } = content
  const [activeIndex, setActiveIndex] = useState(0)

  const ids = Array.isArray(items)
    ? items.map(item => item?.sys?.id).filter((id): id is string => Boolean(id))
    : []

  const { data, isLoading, error } = useQuery({
    queryKey: ['faq-items', ids],
    queryFn: () => getEntriesByIds(ids as string[]),
    enabled: ids.length > 0
  })

  const dataById = Array.isArray(data)
    ? data.reduce((acc: Record<string, any>, entry: any) => {
        acc[entry?.sys?.id] = entry
        return acc
      }, {})
    : {}

  const faqItems =
    Array.isArray(items) && Array.isArray(data)
      ? items.map(item =>
          item?.sys?.id ? { ...item, ...dataById[item.sys.id] } : item
        )
      : items

  const activeFaqs = expandItem ? `item-${activeIndex}` : ''

  const borderColour = ['ink-blue', 'mid-blue'].includes(layoutColour ?? '')
    ? 'white'
    : ['chartreuse', 'white', 'light-grey'].includes(layoutColour ?? '')
      ? 'mid-blue'
      : 'ink-blue'

  if (isLoading)
    return (
      <div className='py-l flex w-full justify-center'>
        <Loader fill={'#f5b6dc'} />
      </div>
    )
  if (error) return <div>Error loading component.</div>

  return (
    // <section>
    // <div className=''>
    <div className='space-y-m md:space-y-l w-full max-w-[86ch]'>
      <h2>{heading}</h2>

      <Accordion
        type='single'
        className='gap-xs space-y-m flex w-full flex-col'
        {...(expandItem && { value: activeFaqs })}
        {...(!expandItem && { collapsible: true })}
      >
        {Array.isArray(faqItems) &&
          faqItems.map((a, i: Index) => (
            <AccordionItem
              key={i}
              value={`item-${i}`}
              className={cn(
                faqVariants({
                  border: borderColour,
                  fill: null
                }),
                'space-y-m relative border-b-1'
              )}
            >
              <AccordionTrigger
                className={cn(
                  ['ink-blue', 'mid-blue'].includes(layoutColour ?? '')
                    ? '*:group-hover:bg-blue-d-80'
                    : '*:group-hover:bg-grey-l',
                  'text-h3-sm sm:text-h3-md lg:text-h3-lg pb-xs-plus cursor-pointer font-bold'
                )}
                onClick={() => setActiveIndex(i)}
              >
                {a?.heading}

                <svg
                  height='1'
                  width='100%'
                  className={cn(
                    'absolute bottom-0 left-0 z-1 origin-left translate-y-[1px] scale-x-0 transition-transform duration-300 group-hover:scale-x-100 group-focus:scale-x-100',
                    activeIndex === i ? 'scale-x-100' : ''
                  )}
                >
                  <rect
                    x='0'
                    y='0'
                    width='100%'
                    height='1'
                    className={cn(
                      faqVariants({
                        border: null,
                        fill: borderColour
                      })
                    )}
                    strokeLinecap='square'
                  />
                </svg>
              </AccordionTrigger>
              <AccordionContent className='pb-m pt-0 text-base'>
                <div
                  className='space-y-xs-plus'
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHtml(a?.copy, {
                      allowedTags: [
                        'em',
                        'strong',
                        'h2',
                        'h3',
                        'h4',
                        'a',
                        'p',
                        'br',
                        'ul',
                        'li'
                      ],
                      allowedAttributes: {
                        '*': ['*']
                      },
                      transformTags: {
                        ul: sanitizeHtml.simpleTransform('ul', {
                          class:
                            'text-tag font-[450] md:text-base list-disc pl-4 space-y-1'
                        }),
                        a: sanitizeHtml.simpleTransform('a', {
                          class: 'cursor-pointer'
                        })
                      }
                    })
                  }}
                ></div>
              </AccordionContent>
            </AccordionItem>
          ))}
      </Accordion>

      <div className='flex justify-end'>
        <TertiaryLink
          data={{
            strongColour: buttons?.strongColour ?? false,
            externalLink: null,
            internalLink: buttons?.internalLink,
            ariaLabel: '',
            newWindow: false,
            label: buttons?.label,
            mobileLabel: buttons?.mobileLabel
          }}
          colour={
            ['ink-blue', 'mid-blue'].includes(layoutColour ?? '')
              ? 'white'
              : 'ink-blue'
          }
        />
      </div>
    </div>
    // </div>
    // </section>
  )
}

export default Faqs
